/**
 * Compliance overview — what is overdue, what is due soon, and what is
 * waiting on a person.
 *
 * Ordered by consequence rather than by module: an overdue statutory filing
 * and an unanswered erasure request both carry a penalty, so they lead.
 */
import { Link } from "react-router-dom";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  StatusBadge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate } from "@/lib/money";
import { DueCell } from "./CompliancePages";
import { daysUntil } from "./types";
import type {
  DataSubjectRequestRow,
  ObligationRow,
  RegulatoryReportRow,
} from "./types";

export function CompliancePage() {
  const obligations = useApiList<ObligationRow>(
    ["compliance", "calendar"],
    "/compliance/calendar",
  );
  const reports = useApiList<RegulatoryReportRow>(
    ["compliance", "regulatory-reports"],
    "/compliance/regulatory-reports",
  );
  const requests = useApiList<DataSubjectRequestRow>(
    ["compliance", "data-subject-requests"],
    "/compliance/data-subject-requests",
  );

  if (obligations.isLoading) return <Skeleton rows={10} />;

  const obligationRows = obligations.data?.items ?? [];
  const overdue = obligationRows.filter(
    (o) => daysUntil(o.due_date) < 0 && o.status !== "Completed",
  );
  const dueSoon = obligationRows.filter((o) => {
    const days = daysUntil(o.due_date);
    return days >= 0 && days <= 30 && o.status !== "Completed";
  });
  const openRequests = (requests.data?.items ?? []).filter(
    (r) => r.status !== "Completed",
  );

  return (
    <div>
      <PageHeader
        title="Compliance"
        subtitle="Statutory obligations, regulatory filings and data subject requests."
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <StatCard
          label="Overdue"
          value={overdue.length}
          meta={
            overdue.length > 0 ? "Past the statutory date" : "Nothing overdue"
          }
          tone={overdue.length > 0 ? "negative" : "positive"}
        />
        <StatCard label="Due within 30 days" value={dueSoon.length} />
        <StatCard label="Reports prepared" value={reports.data?.total ?? 0} />
        <StatCard
          label="Open data requests"
          value={openRequests.length}
          meta={
            openRequests.length > 0
              ? "Each carries a statutory clock"
              : undefined
          }
          tone={openRequests.length > 0 ? "negative" : undefined}
        />
      </div>

      {obligationRows.length === 0 ? (
        <EmptyState
          title="No compliance obligations recorded"
          message="Obligations arrive with the country pack for the jurisdictions you operate in — VAT returns, withholding filings, statutory accounts. Install a country pack to populate the calendar."
          action={
            <Link to="/app/setup/country-packs">
              <span className="atlas-btn atlas-btn--primary">
                Country packs
              </span>
            </Link>
          }
        />
      ) : (
        <>
          {overdue.length > 0 && (
            <Card title={`Overdue (${overdue.length})`} className="mb-4">
              <DataTable<ObligationRow>
                rows={overdue}
                columns={[
                  { key: "title", label: "Obligation" },
                  { key: "obligation_type", label: "Type" },
                  {
                    key: "due_date",
                    label: "Was due",
                    render: (r) => <DueCell due={r.due_date} />,
                  },
                  {
                    key: "status",
                    label: "Status",
                    render: (r) => <StatusBadge status={r.status} />,
                  },
                ]}
              />
            </Card>
          )}

          <Card title="Coming up">
            {dueSoon.length === 0 ? (
              <EmptyState title="Nothing due in the next 30 days" />
            ) : (
              <DataTable<ObligationRow>
                rows={dueSoon}
                columns={[
                  { key: "title", label: "Obligation" },
                  { key: "obligation_type", label: "Type" },
                  {
                    key: "due_date",
                    label: "Due",
                    render: (r) => <DueCell due={r.due_date} />,
                  },
                  {
                    key: "status",
                    label: "Status",
                    render: (r) => <StatusBadge status={r.status} />,
                  },
                ]}
              />
            )}
            <div className="mt-4">
              <Link to="/app/compliance/calendar">Full calendar</Link>
            </div>
          </Card>
        </>
      )}

      {openRequests.length > 0 && (
        <Card
          title={`Open data subject requests (${openRequests.length})`}
          className="mt-4"
        >
          <DataTable<DataSubjectRequestRow>
            rows={openRequests.slice(0, 6)}
            columns={[
              { key: "request_type", label: "Type" },
              { key: "subject_email", label: "Subject" },
              {
                key: "requested_at",
                label: "Received",
                render: (r) => formatDate(r.requested_at),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
          <Link to="/app/compliance/data-subject-requests">All requests</Link>
        </Card>
      )}
    </div>
  );
}
